import {
  useEffect,
  SetStateAction,
  Dispatch,
  useCallback,
  useMemo,
  useState,
} from "react";
// hooks
import { useOrchidDriveUploadTasks } from "contexts/orchid-drive";
// types
import type { Task, WatchTask } from "../..";
import type { OrchidDriveUploadTaskWithEntries } from "contexts/orchid-drive/types";
import type { UploadTaskAbortFunc } from "contexts/orchid-drive/components/orchidDriveProvider";
// components
import UploadTaskStatusMemberDrive from "./components/upload-task-status-member-drive";
import UploadTaskStatusPatientCharts from "./components/upload-task-status-patient-charts";
import CancelUploadConfirmModal from "../cancel-upload-confirm-modal";
// constants
import messages from "messages";
import { TASK_STATUS } from "../../constants";
// hooks
import useToggleModal from "hooks/useToggleModal";

type Props = {
  storageDrive: StorageDrive;
  watchTasks: WatchTask[];
  setWatchTasks: Dispatch<SetStateAction<WatchTask[]>>;
  onError: (arg: string | Error) => void;
};

export type UploadTaskStatusStorageDriveProps = {
  tasks: (Pick<OrchidDriveUploadTaskWithEntries, "fileIds"> & Task)[];
  setAbortFn: (arg: UploadTaskAbortFunc) => void;
  onRemoveUpload: (arg: any) => void;
  onHide: () => void;
};

const UploadTaskStatus = ({
  storageDrive,
  watchTasks,
  setWatchTasks,
  onError,
}: Props) => {
  // TODO: need error by upload task id
  const { uploadTasks, uploadTasksError } = useOrchidDriveUploadTasks();

  useEffect(() => {
    if (uploadTasksError) {
      return onError(messages.oops);
    }
  }, [onError, uploadTasksError]);

  const tasks = useMemo(() => {
    const res = uploadTasks.reduce((prev, el) => {
      if (el.uploadTask) {
        const watchTask = watchTasks.find(
          (task) => task.id === el.uploadTask?.uploadTaskId,
        );
        if (watchTask) {
          prev.push({
            // TODO: current path is only dir id, however, since the folder may be a path of /a/b/c, while in the /a/ we cannot know folder /c/'s name
            // uploadFolderName:
            //   el.uploadTask.files.length > 1
            //     ? ""
            //     : (el.uploadTask.files?.[0]?.path || []).join("/"),
            size: watchTask.size,
            fileName:
              watchTask.name ||
              el.uploadTask.files.map((el) => el.name).join(" "),
            isFolder: watchTask.isFolder,
            fileIds: el.uploadTask.fileIds,
            id: el.uploadTask.uploadTaskId,
            percent: el.uploadTask.uploadPercent ?? 0,
            busy:
              !el.uploadTask.uploadAbandonedDatetime &&
              !el.uploadTask.uploadFinalizedDatetime,
            state: el.uploadTask.uploadAbandonedDatetime
              ? TASK_STATUS.cancelled
              : el.uploadTask.uploadFinalizedDatetime
              ? TASK_STATUS.complete
              : TASK_STATUS.uploading,
          });
        }
      }

      return prev;
    }, [] as UploadTaskStatusStorageDriveProps["tasks"]);

    return res;
  }, [uploadTasks, watchTasks]);

  const hasBusyTask = useMemo(() => tasks.some((el) => el.busy), [tasks]);

  const [abortFn, setAbortFn] = useState<UploadTaskAbortFunc>();

  const onRemoveUpload = useCallback(
    async (uploadTask: any) => {
      if (!abortFn) {
        return;
      }
      try {
        await abortFn({ uploadTaskId: uploadTask.id });
      } catch {
        return onError(messages.oops);
      }
    },
    [onError, abortFn],
  );

  const { show, open, close } = useToggleModal(false);

  // remove all tasks in view, should consider if the task busy state
  const onCloseUploads = useCallback(async () => {
    try {
      if (!abortFn) {
        return;
      }

      await Promise.all(
        tasks.map(async (uploadTask) => {
          try {
            if (uploadTask.percent !== 100) {
              await abortFn({ uploadTaskId: uploadTask.id });
            }

            setWatchTasks((s) => {
              const index = s.findIndex((el) => el.id === uploadTask.id);
              if (index < 0) {
                return s;
              }

              return [...s.slice(0, index), ...s.slice(index + 1)];
            });

            return Promise.resolve();
          } catch (error) {
            return Promise.reject(error);
          }
        }),
      );
    } catch {
      onError && onError(messages.oops);
    } finally {
      close();
    }
  }, [close, onError, setWatchTasks, tasks, abortFn]);

  const onHide = useCallback(
    () => (hasBusyTask ? open() : onCloseUploads()),
    [hasBusyTask, onCloseUploads, open],
  );

  return (
    <>
      {storageDrive === "member-drive" ? (
        <UploadTaskStatusMemberDrive
          tasks={tasks}
          onRemoveUpload={onRemoveUpload}
          setAbortFn={(v) => setAbortFn(() => v)}
          onHide={onHide}
        />
      ) : storageDrive === "patient-chart" ? (
        <UploadTaskStatusPatientCharts
          tasks={tasks}
          onRemoveUpload={onRemoveUpload}
          setAbortFn={(v) => setAbortFn(() => v)}
          onHide={onHide}
        />
      ) : null}

      {/* confirm remove all tasks, include those in progress */}
      <CancelUploadConfirmModal
        show={show}
        onCancel={close}
        onConfirm={onCloseUploads}
      />
    </>
  );
};
export default UploadTaskStatus;
